import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, CalendarPlus, Loader2, Trash2, Users } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useCrewRoster } from '@/lib/crewRoster';
import {
  isoDate,
  createAssignment,
  updateAssignment,
  deleteAssignment,
} from '@/lib/scheduleData';
import { toast } from 'sonner';

/**
 * Add or edit a single schedule assignment. When `assignment` is provided we
 * are in edit mode; otherwise it's an add-for-this-date dialog.
 */
const VIRTUAL_OTHER = '__other__';
const VIRTUAL_MEETING = '__meeting__';
const isVirtualJob = (id) => id === VIRTUAL_OTHER || id === VIRTUAL_MEETING;

export const AssignmentDialog = ({
  open,
  onOpenChange,
  date,
  jobs = [],
  assignment = null,
  onSaved,
  onDeleted,
}) => {
  const { t } = useTranslation();
  // Live crew roster (active employees only). When admins add/disable
  // employees in Settings the assignment dropdown updates immediately.
  const { roster: activeCrew } = useCrewRoster({ activeOnly: true });
  // Translation-aware virtual labels (rebuilt on language change automatically
  // because they're computed inline from t()).
  const VIRTUAL_JOB_LABELS = {
    [VIRTUAL_OTHER]: t('schedule.dialog.otherOption'),
    [VIRTUAL_MEETING]: t('schedule.dialog.meetingOption'),
  };
  const isEdit = !!assignment;
  const initialJob = useMemo(() => {
    if (assignment?.job_id) return assignment.job_id;
    return '';
  }, [assignment]);
  // Detect a stored virtual assignment so we can rehydrate the "Other / Meeting"
  // selection when re-opening for edit. Recognizes both the persisted English
  // labels (legacy data) and the current locale's labels.
  const detectVirtual = (raw) => {
    if (!raw) return null;
    const otherLabel = t('schedule.dialog.otherOption');
    const meetingLabel = t('schedule.dialog.meetingOption');
    if (
      raw === VIRTUAL_OTHER ||
      raw === 'Other' ||
      raw === otherLabel ||
      raw.startsWith('Other:') ||
      raw.startsWith(`${otherLabel}:`)
    )
      return VIRTUAL_OTHER;
    if (
      raw === VIRTUAL_MEETING ||
      raw === 'Meeting' ||
      raw === meetingLabel ||
      raw.startsWith('Meeting:') ||
      raw.startsWith(`${meetingLabel}:`)
    )
      return VIRTUAL_MEETING;
    return null;
  };
  const initialVirtual = detectVirtual(initialJob);
  const initialCustom =
    initialVirtual && typeof initialJob === 'string' && initialJob.includes(':')
      ? initialJob.split(':').slice(1).join(':').trim()
      : '';
  const [jobId, setJobId] = useState(initialVirtual || initialJob);
  const [customLabel, setCustomLabel] = useState(initialCustom);
  const [crew, setCrew] = useState(assignment?.crew || []);
  const [note, setNote] = useState(assignment?.note || '');
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      const raw = assignment?.job_id || '';
      const virt = detectVirtual(raw);
      setJobId(virt || raw);
      setCustomLabel(virt && raw.includes(':') ? raw.split(':').slice(1).join(':').trim() : '');
      setCrew(assignment?.crew || []);
      setNote(assignment?.note || '');
      setError('');
    }
    // detectVirtual is a local closure that only reads from the stable i18next
    // `t` function; including it would cause the effect to re-run on every
    // render of the dialog.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, assignment]);

  const toggleCrew = (name) => {
    setCrew((cur) =>
      cur.includes(name) ? cur.filter((c) => c !== name) : [...cur, name]
    );
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    if (!jobId) {
      setError(t('schedule.dialog.pickJob'));
      return;
    }
    if (crew.length === 0) {
      setError(t('schedule.dialog.selectCrewMember'));
      return;
    }
    setSubmitting(true);
    try {
      // Resolve final jobId + jobName based on whether the user picked a real
      // job or one of the virtual options (Other / Meeting).
      let finalJobId;
      let finalJobName;
      if (isVirtualJob(jobId)) {
        const baseLabel = VIRTUAL_JOB_LABELS[jobId];
        const trimmed = customLabel.trim();
        finalJobName = trimmed ? `${baseLabel}: ${trimmed}` : baseLabel;
        // Persist the human-readable label as job_id since job_id is free-form text.
        finalJobId = finalJobName;
      } else {
        const job = jobs.find((j) => j.id === jobId);
        finalJobId = jobId;
        finalJobName = job?.name || jobId;
      }
      const payload = {
        date,
        jobId: finalJobId,
        jobName: finalJobName,
        crew,
        note,
      };
      let saved;
      if (isEdit) {
        saved = await updateAssignment(assignment.id, {
          date,
          jobId: finalJobId,
          jobName: finalJobName,
          crew,
          note,
        });
      } else {
        saved = await createAssignment(payload);
      }
      toast.success(isEdit ? t('schedule.dialog.assignmentUpdated') : t('schedule.dialog.assignmentAdded'));
      onSaved?.(saved);
      onOpenChange?.(false);
    } catch (err) {
      setError(err?.message || t('schedule.dialog.saveFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!assignment?.id) return;
    setDeleting(true);
    try {
      await deleteAssignment(assignment.id);
      toast.success(t('schedule.dialog.assignmentRemoved'));
      onDeleted?.(assignment);
      onOpenChange?.(false);
    } catch (e) {
      toast.error(e?.message || t('schedule.dialog.deleteFailed'));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[hsl(var(--card))] border-[hsl(var(--hairline))] max-w-md"
        data-testid="assignment-dialog"
      >
        <DialogHeader>
          <div className="flex items-center gap-2 text-[hsl(var(--primary))]">
            <CalendarPlus size={16} strokeWidth={2} />
            <span className="text-[10px] font-medium uppercase tracking-[0.28em] text-muted-foreground">
              {isEdit ? t('schedule.dialog.editAssignment') : t('schedule.dialog.scheduleCrew')}
            </span>
          </div>
          <DialogTitle className="text-foreground tracking-tight">
            {isoDate(date)}
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            {isEdit
              ? t('schedule.dialog.descriptionEdit')
              : t('schedule.dialog.descriptionAdd')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSave} className="space-y-4 mt-1" noValidate>
          <div className="space-y-2">
            <Label className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
              {t('schedule.dialog.jobLabel')} *
            </Label>
            <Select value={jobId} onValueChange={setJobId}>
              <SelectTrigger
                className="h-10 bg-[hsl(var(--muted))] border-border"
                data-testid="assignment-job"
              >
                <SelectValue placeholder={t('schedule.dialog.jobPlaceholder')} />
              </SelectTrigger>
              <SelectContent className="bg-[hsl(var(--card))] border-border max-h-72">
                {jobs.length === 0 ? (
                  <div className="px-3 py-2 text-xs text-muted-foreground">
                    {t('schedule.dialog.noActiveJobs')}
                  </div>
                ) : (
                  jobs.map((j) => (
                    <SelectItem key={j.id} value={j.id}>
                      {j.name}
                    </SelectItem>
                  ))
                )}
                <div className="my-1 border-t border-border" aria-hidden />
                <SelectItem
                  value={VIRTUAL_OTHER}
                  data-testid="assignment-job-option-other"
                >
                  {t('schedule.dialog.otherOption')}
                </SelectItem>
                <SelectItem
                  value={VIRTUAL_MEETING}
                  data-testid="assignment-job-option-meeting"
                >
                  {t('schedule.dialog.meetingOption')}
                </SelectItem>
              </SelectContent>
            </Select>
            {isVirtualJob(jobId) ? (
              <Input
                value={customLabel}
                onChange={(e) => setCustomLabel(e.target.value)}
                placeholder={
                  jobId === VIRTUAL_MEETING
                    ? t('schedule.dialog.meetingTopicPlaceholder')
                    : t('schedule.dialog.otherDetailPlaceholder')
                }
                className="h-9 bg-[hsl(var(--muted))] border-border text-sm"
                data-testid="assignment-job-custom"
              />
            ) : null}
          </div>

          <div className="space-y-2">
            <Label className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground inline-flex items-center gap-1.5">
              <Users size={12} />
              {t('schedule.dialog.crewLabel')} *
            </Label>
            <div
              className="flex flex-wrap gap-2"
              data-testid="assignment-crew"
            >
              {activeCrew.map((c) => {
                const active = crew.includes(c.name);
                return (
                  <button
                    type="button"
                    key={c.id}
                    onClick={() => toggleCrew(c.name)}
                    data-testid={`assignment-crew-${c.id}`}
                    aria-pressed={active}
                    className={cn(
                      'h-9 px-3 rounded-md border text-xs uppercase tracking-[0.16em] font-medium transition-colors',
                      active
                        ? 'border-[hsl(var(--primary))]/40 bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))]'
                        : 'border-border bg-[hsl(var(--muted))] text-muted-foreground hover:text-foreground hover:border-[hsl(var(--primary))]/30'
                    )}
                  >
                    {c.name}
                  </button>
                );
              })}
            </div>
            {crew.length > 0 ? (
              <div className="text-[11px] text-muted-foreground">
                {crew.map((n) => (
                  <Badge
                    key={n}
                    variant="outline"
                    className="mr-1.5 mt-1.5 uppercase text-[10px] tracking-[0.16em] bg-[hsl(var(--muted))] text-foreground border-border"
                  >
                    {n}
                  </Badge>
                ))}
              </div>
            ) : null}
          </div>

          <div className="space-y-2">
            <Label className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
              {t('schedule.dialog.noteLabel')}
            </Label>
            <Textarea
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t('schedule.dialog.notePlaceholder')}
              className="bg-[hsl(var(--muted))] border-border text-foreground placeholder:text-muted-foreground/60 focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]/40 resize-none"
              data-testid="assignment-note"
            />
          </div>

          {error ? (
            <Alert
              className="border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/10 text-foreground"
              data-testid="assignment-error"
            >
              <AlertCircle className="h-4 w-4 text-[hsl(var(--destructive))]" />
              <AlertDescription className="text-sm">{error}</AlertDescription>
            </Alert>
          ) : null}

          <DialogFooter className="gap-2 sm:gap-3 pt-2">
            {isEdit ? (
              <Button
                type="button"
                variant="ghost"
                onClick={handleDelete}
                disabled={submitting || deleting}
                className="mr-auto text-[hsl(var(--destructive))] hover:bg-[hsl(var(--destructive))]/10"
                data-testid="assignment-delete"
              >
                {deleting ? (
                  <Loader2 size={14} className="mr-2 animate-spin" />
                ) : (
                  <Trash2 size={14} className="mr-2" />
                )}
                {t('schedule.dialog.delete')}
              </Button>
            ) : null}
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange?.(false)}
              disabled={submitting || deleting}
              className="text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--muted))]"
            >
              {t('schedule.dialog.cancel')}
            </Button>
            <Button
              type="submit"
              disabled={submitting || deleting}
              className="bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-[0.16em] text-xs"
              data-testid="assignment-submit"
            >
              {submitting ? (
                <Loader2 size={14} className="mr-2 animate-spin" />
              ) : null}
              {isEdit ? t('schedule.dialog.saveChanges') : t('schedule.dialog.addCta')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AssignmentDialog;
